import React, { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';

import { Box, Link, Paragraph, Text } from '@rover/kibble/core';
import { ConfigureFieldInterface, ZodSchemaOrEffects } from '@rover/kibble/forms';
import CheckboxField from '@rover/kibble/forms/fields/CheckboxField';
import { CheckboxFieldOptionConfig } from '@rover/kibble/forms/fields/CheckboxField/CheckboxField.common';
import SelectField from '@rover/kibble/forms/fields/SelectField';

import { getCountryOptions } from './utils/countryUtils';

export interface CountrySectionProps<Z extends ZodSchemaOrEffects> {
  countryField: ConfigureFieldInterface<Z>;
  countryCheckboxesField: ConfigureFieldInterface<Z>;
  locale?: string;
  disableCountry?: boolean;
  getValues?: (name: string) => string;
}

const CountrySection = <Z extends ZodSchemaOrEffects>({
  countryField,
  countryCheckboxesField,
  locale = 'en-us',
  disableCountry = false,
}: CountrySectionProps<Z>): JSX.Element => {
  const [countryOptions, setCountryOptions] = useState<Array<{ value: string; label: string }>>([]);

  const { field: countryFieldValue, fieldState: countryFieldState } = useController({
    control: countryField.control,
    name: countryField.name,
  });

  // Load localized country options
  useEffect(() => {
    const loadCountryOptions = async (): Promise<void> => {
      const options = await getCountryOptions(locale);
      setCountryOptions(options);
    };

    loadCountryOptions();
  }, [locale]);

  const selectedCountry = countryOptions.find((option) => option.value === countryFieldValue.value);
  const countryName = selectedCountry?.label;

  const options = [
    {
      primaryLabel: (
        <Trans>
          Yes, I agree to the Rover{' '}
          <Link
            asInlineLink
            href="https://www.rover.com/terms/tos/"
            target="_blank"
            onClick={(e?) => e?.stopPropagation()}
          >
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link
            asInlineLink
            href="https://www.rover.com/terms/privacy/"
            target="_blank"
            onClick={(e?) => e?.stopPropagation()}
          >
            Privacy Statement
          </Link>{' '}
          and confirm that I am 18 years of age or older.
        </Trans>
      ),
      value: 'agreeTOS',
    },
    {
      primaryLabel: t`Yes, I want to receive marketing emails and messages from Rover and its affiliates. I can unsubscribe at any time.`,
      value: 'emailOptIn',
    },
  ] as CheckboxFieldOptionConfig[];

  return (
    <>
      <Box>
        <SelectField
          {...countryField}
          primaryLabel={t`Country`}
          options={countryOptions}
          placeholder={t`Select country`}
          disabled={disableCountry}
          onChange={(selectedValue) => {
            countryFieldValue.onChange(selectedValue);
          }}
        />
        {disableCountry && (
          <Paragraph size="100" textColor="tertiary" mt="1x">
            <Trans>
              Please contact{' '}
              <Link
                size="50"
                fontWeight="regular"
                asInlineLink
                href="https://support.rover.com/hc/en-us/requests/new"
              >
                Rover Support
              </Link>{' '}
              to change your home country.
            </Trans>
          </Paragraph>
        )}
      </Box>
      {countryFieldState.isDirty && (
        <Box mt="4x" background="highlight" p="4x" borderRadius="secondary" pl="6x">
          <Text size="200" fontWeight="semibold" mb="1x">
            {t`Moving to ${countryName}.`}
          </Text>
          <Paragraph size="200" mb="0x">
            {t`In order to use Rover in ${countryName}, you need to agree to updated terms & conditions.`}
          </Paragraph>
          <Box pr="6x">
            <CheckboxField options={options} {...countryCheckboxesField} primaryLabel="" />
          </Box>
        </Box>
      )}
    </>
  );
};

export default CountrySection;
